import { createFeatureSelector, createSelector } from '@ngrx/store';
import { FeaturesState } from './features.reducer';
import { RequestStatus } from '../../enums/request-status.enum';
import { FeatureCode, FEATURE_CODES } from '../models/feature.model';

export const selectFeaturesState = createFeatureSelector<FeaturesState>('features');

export const selectFeatures = createSelector(
  selectFeaturesState,
  (state) => state.features
);

export const selectFeaturesStatus = createSelector(
  selectFeaturesState,
  (state) => state.status
);

export const selectFeaturesError = createSelector(
  selectFeaturesState,
  (state) => state.error
);

export const selectFeaturesLoading = createSelector(
  selectFeaturesStatus,
  (status) => status === RequestStatus.Loading
);

export const selectFeaturesLoaded = createSelector(
  selectFeaturesStatus,
  (status) => status === RequestStatus.Success
);

export const selectFeatureCodes = createSelector(
  selectFeatures,
  (features) => {
    if (!Array.isArray(features)) {
      return [];
    }

    return features.map(({ code }) => code);
  }
);

export const selectAccountFeatureCodes = createSelector(
  selectFeaturesState,
  (state) => state.accountFeatureCodes ?? []
);

export const createHasFeatureSelector = (code: FeatureCode) =>
  createSelector(selectFeatureCodes, (codes) => codes.includes(code));

export const selectHasObservePointFeature = createSelector(
  selectFeatureCodes,
  (codes) => codes.includes(FEATURE_CODES.OBSERVE_POINT)
);

export const selectHasTrustedDestinationFeature = createSelector(
  selectFeatureCodes,
  (codes) => codes.includes(FEATURE_CODES.TRUSTED_DESTINATIONS)
);

export const selectHasAccountTrustedDestinationFeature = createSelector(
  selectAccountFeatureCodes,
  (codes) => codes.includes(FEATURE_CODES.TRUSTED_DESTINATIONS)
);

export const selectHasAccountPendingLinksFeature = createSelector(
  selectAccountFeatureCodes,
  (codes) => codes.includes(FEATURE_CODES.PENDING_LINKS)
);

export const selectHasELinksFeature = createSelector(
  selectFeatureCodes,
  (codes) => codes.includes(FEATURE_CODES.E_LINKS)
);

export const selectHasPowerPagesFeature = createSelector(
  selectFeatureCodes,
  (codes) => codes.includes(FEATURE_CODES.POWER_PAGES)
);

export const selectHasIpExclusionsFeature = createSelector(
  selectFeatureCodes,
  (codes) => codes.includes(FEATURE_CODES.IP_EXCLUSIONS)
);

export const selectHasGs1LinksFeature = createSelector(
  selectFeatureCodes,
  selectAccountFeatureCodes,
  (domainCodes, accountCodes) =>
    domainCodes.includes(FEATURE_CODES.GS1_LINK_TEMPLATES) ||
    accountCodes.includes(FEATURE_CODES.GS1_LINK_TEMPLATES)
);

export const selectUseReactUI = createSelector(
  selectFeatureCodes,
  (codes) => codes.includes(FEATURE_CODES.USE_REACT_UI)
);

export const selectFeaturesDecisionReady = createSelector(
  selectFeaturesStatus,
  selectFeatures,
  (status, features) => status !== RequestStatus.Loading || features !== null
);
